import { Component } from '@theme/component';
import { isMobileAppWebView } from '@theme/utilities';

/**
 * First-Time Customer Discount Popup — MA-1387.
 *
 * The section applies the eligibility rules Liquid can see and renders nothing for an
 * ineligible customer. This element covers the rest: the portal_id cookie (the only
 * affiliation signal an anonymous visitor carries), auto-open, and revealing the
 * announcement-bar reopen link, which ships hidden for the same reason.
 *
 * Shown once per session, per Mackenzie's AC rather than the spec's 1-day cookie, and
 * matches country-mismatch-popup.js. The session is marked when the popup auto-opens,
 * not only when it is closed: a visitor who leaves the page with it still open (back
 * button, reload, typed URL) must not meet it again on the next page (MA-2458). Closing
 * it and clicking through it keep marking the session too, and the reopen link and the
 * post-captcha code reveal open the dialog directly, so neither is affected.
 *
 * @typedef {object} Refs
 * @property {import('./dialog.js').default} dialogComponent
 *
 * @extends Component<Refs>
 */
class FirstTimeDiscountPopup extends Component {
  requiredRefs = ['dialogComponent'];

  /** sessionStorage key marking the popup as shown or dismissed this session. */
  static SESSION_KEY = 'ma_first_time_discount_dismissed';

  /**
   * Announcement-bar slides are rich text, so the reopen trigger there can only be a
   * plain link. The data attribute covers surfaces that can carry an attribute.
   */
  static TRIGGER_SELECTOR = '[data-first-time-discount-trigger], a[href="#reopen-discount"]';

  connectedCallback() {
    super.connectedCallback();

    // In the MA app's webview the app owns the promotional surface, so the popup is
    // suppressed outright rather than hidden — bailing before #revealTriggers() also
    // leaves the announcement-bar reopen link in its shipped hidden state.
    // snippets/mobile-app-webview.liquid sets the class in <head>, so it is already on
    // the element by the time this module runs (MA-1601).
    if (isMobileAppWebView()) return;

    // /challenge renders inside the theme layout, so this global section mounts
    // there too; auto-opening over the hCaptcha traps the visitor in a resubmit loop.
    if (FirstTimeDiscountPopup.#onChallengePage()) return;

    // Bail before revealing anything: a portal visitor gets no reopen link either.
    if (this.#onPortal()) return;

    this.#revealTriggers();
    document.addEventListener('click', this.#handleTriggerClick);

    this.addEventListener('click', this.#markDismissedOnNavigate);
    this.refs.dialogComponent.addEventListener('dialog:close', this.#markDismissed);
    this.refs.dialogComponent.addEventListener('dialog:open', this.#parkFocusOnDialog);

    // Captured before the success watch below, which may mark the session
    // dismissed synchronously — the page that reveals the code must still open.
    const alreadyDismissed = this.#dismissedThisSession();

    // On a challenge-return page with the confirmation already visible the watch
    // opens the dialog itself; opening it again here would queue a second
    // showModal() before the first one's frame has run.
    const openedForCode = this.#dismissOnSignupSuccess();

    if (alreadyDismissed || openedForCode) return;

    // Once per session means once: mark it before opening, so leaving without closing
    // does not bring it back on the next page.
    this.#markDismissed();
    this.refs.dialogComponent.showDialog();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleTriggerClick);
    this.removeEventListener('click', this.#markDismissedOnNavigate);
    this.refs.dialogComponent?.removeEventListener('dialog:close', this.#markDismissed);
    this.#successObserver?.disconnect();
    this.#successObserver = null;
  }

  /** @type {MutationObserver | null} */
  #successObserver = null;

  /**
   * The success CTA navigates with the dialog still open, so dialog:close never
   * fires and without this every following page re-opens the entry form.
   *
   * The confirmation is also the only place the code appears, and the native
   * (spam-challenge) submit reaches it on a fresh page load. If the visitor had
   * closed the popup earlier in the session, the guard in connectedCallback
   * skipped showDialog() on that return page and the code sat in a closed
   * dialog. So whenever the confirmation is, or becomes, visible: mark the
   * session dismissed as before, and make sure the dialog is open. showDialog()
   * is a no-op when it already is, so the async route is unaffected (MA-2458).
   *
   * @returns {boolean} true when the confirmation was already visible and this
   *   opened the dialog synchronously, so the caller must not open it again.
   */
  #dismissOnSignupSuccess() {
    const success = this.querySelector('[data-signup-success]');
    if (!success) return false;

    if (!success.hidden) {
      this.#markDismissed();
      this.refs.dialogComponent.showDialog();
      return true;
    }

    this.#successObserver = new MutationObserver(() => {
      if (success.hidden) return;

      this.#markDismissed();
      this.refs.dialogComponent.showDialog();
      this.#successObserver?.disconnect();
      this.#successObserver = null;
    });
    this.#successObserver.observe(success, { attributes: true, attributeFilter: ['hidden'] });
    return false;
  }

  /**
   * True on Shopify's /challenge interstitial (optionally locale-prefixed).
   * Anchored so handles that merely end in "challenge" don't match.
   * @returns {boolean}
   */
  static #onChallengePage() {
    return /^(?:\/[a-z]{2,3}(?:-[a-z0-9]{2,8})*)?\/challenge\/?$/i.test(window.location.pathname);
  }

  /**
   * True when the visitor is tied to a UFO's portal, from either the logged-in
   * customer's metafield or the cookie an anonymous visitor arrives with.
   *
   * Only a portal different from the default disqualifies: every registered customer
   * carries the default one and those visitors keep the offer. Mirrors the section's
   * server-side gate, including treating a blank default as "any portal disqualifies".
   * @returns {boolean}
   */
  #onPortal() {
    const fallback = (this.dataset.defaultPortal || '').toLowerCase();

    const portals = [];
    if (window.customerPortalId) portals.push(String(window.customerPortalId));
    // A UFO link arrives as ?portal_id= and portal-manager.js only writes it to the
    // cookie on DOMContentLoaded, after this module has already decided. Read the
    // param directly so the very first page view of a portal link is gated too,
    // not just the ones after the cookie exists (MA-2458).
    const fromUrl = new URLSearchParams(window.location.search).get('portal_id');
    if (fromUrl) portals.push(fromUrl);
    for (const entry of document.cookie.split('; ')) {
      if (entry.startsWith('portal_id=')) portals.push(decodeURIComponent(entry.slice('portal_id='.length)));
    }

    return portals.some((portal) => portal.trim().toLowerCase() !== fallback || fallback === '');
  }

  /**
   * Delegated so triggers rendered after this element connects (the announcement bar
   * re-renders on theme-editor changes) still work without rebinding.
   * @param {MouseEvent} event
   */
  #handleTriggerClick = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (!target?.closest(FirstTimeDiscountPopup.TRIGGER_SELECTOR)) return;

    event.preventDefault();
    this.refs.dialogComponent.showDialog();
  };

  /** Show the reopen links, which ship hidden so ineligible visitors never see them. */
  #revealTriggers() {
    document.querySelectorAll(FirstTimeDiscountPopup.TRIGGER_SELECTOR).forEach((trigger) => {
      trigger.removeAttribute('hidden');
      trigger.classList.add('ftd-revealed');
    });
  }

  /** @returns {boolean} */
  #dismissedThisSession() {
    try {
      return Boolean(sessionStorage.getItem(FirstTimeDiscountPopup.SESSION_KEY));
    } catch {
      // sessionStorage unavailable (private mode, blocked storage) — show the popup
      // rather than suppress it, matching country-mismatch-popup.js.
      return false;
    }
  }

  /**
   * showModal() auto-focuses the first focusable child, which here is the close button,
   * and WebKit paints a focus ring for that. The popup therefore opened with a ring
   * around the X for visitors who never touched a keyboard (MA-2347). Park focus on the
   * dialog instead: the modal still traps focus, assistive tech still announces the
   * dialog, and the first Tab lands on the close button with a deliberate ring.
   */
  #parkFocusOnDialog = () => {
    const dialog = this.querySelector('dialog.first-time-discount-popup');
    if (!dialog) return;

    dialog.focus({ preventScroll: true });
    // WebKit can run showModal()'s own focusing step after this event rather than
    // before it, which would hand focus back to the close button and put the ring
    // on screen again. Re-assert once the frame has settled.
    requestAnimationFrame(() => {
      if (dialog.open) dialog.focus({ preventScroll: true });
    });
  };

  /**
   * Following a link out of the popup dismisses it for the session.
   *
   * Dismissal is otherwise only recorded on dialog:close, and a link navigates
   * with the dialog still open, so that event never fires. The next page then
   * auto-opened the popup again over whatever the visitor had asked for: the
   * terms page appeared for an instant and the takeover covered it, which reads
   * as "the link does nothing" (MA-2572). #dismissOnSignupSuccess() already
   * covered the confirmation's CTA, but the entry state's terms link had nothing.
   *
   * Handled here rather than per link so every link the section renders now, or
   * gains later, is covered. In-page anchors (the announcement-bar reopen link is
   * one) never navigate away, and a handler that has already called
   * preventDefault is not navigating either, so both are left alone.
   *
   * @param {MouseEvent} event
   */
  #markDismissedOnNavigate = (event) => {
    if (event.defaultPrevented) return;

    const link = /** @type {HTMLElement | null} */ (event.target)?.closest?.('a[href]');
    if (!link || !this.contains(link)) return;

    // A new tab or window leaves this page standing, so the popup is still the
    // thing in front of the visitor and must not be marked dismissed.
    const target = link.getAttribute('target');
    if (target && target !== '_self') return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    if (link.getAttribute('href')?.startsWith('#')) return;

    this.#markDismissed();
  };

  #markDismissed = () => {
    try {
      sessionStorage.setItem(FirstTimeDiscountPopup.SESSION_KEY, '1');
    } catch {
      // Best-effort: without storage the popup reappears next navigation.
    }
  };
}

if (!customElements.get('first-time-discount-popup')) {
  customElements.define('first-time-discount-popup', FirstTimeDiscountPopup);
}
